import React, { useState, useEffect, useCallback } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import Header from './components/Header';
import Footer from './components/Footer';
import Home from './pages/Home';
import Result from './pages/Result';
import About from './pages/About';
import Contact from './pages/Contact';
import Login from './components/Login';
import Register from './components/Register';
import UserDashboard from './components/UserDashboard';
import AdminDashboard from './components/AdminDashboard';
import ModernAdminDashboard from './components/ModernAdminDashboard';
import AdminRouter from './pages/admin/AdminRouter';
import ProjectDetail from './pages/ProjectDetail';
import ShortLinks from './pages/ShortLinks';
import ShortLinkDetail from './pages/ShortLinkDetail';
import DashboardPage from './pages/DashboardPage';
import InternalAds from './pages/InternalAds';
import InternalAdsDetail from './pages/InternalAdsDetail';
import DashboardLayout from './components/DashboardLayout';
import VerifyEmail from './pages/VerifyEmail';
import PlayStoreEmails from './pages/PlayStoreEmails';
import PlayStoreContact from './pages/PlayStoreContact';
import Unsubscribe from './pages/Unsubscribe';
import UnsubscribeSimple from './pages/UnsubscribeSimple';
import UnsubscribeTest from './pages/UnsubscribeTest';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import LandingPages from './pages/LandingPages';
import LandingPageView from './pages/LandingPageView';
import LandingPageRoute from './pages/LandingDocumentView';
import UpgradeModal from './components/UpgradeModal';
import FeedbackSupportModal from './components/FeedbackSupportModal';
import useScrollToTop from './hooks/useScrollToTop';
import { isTokenValid, getUser, getApiUrl } from './utils/tokenUtils';
import './App.css';

function App() {
  return (
    <Router
      future={{
        v7_startTransition: true,
        v7_relativeSplatPath: true
      }}
    >
      <AppContent />
    </Router>
  );
}

function AppContent() {
  useScrollToTop();
  const [user, setUser] = useState(() => {
    try {
      const savedUser = localStorage.getItem('user');
      const savedToken = localStorage.getItem('token');
      if (savedUser && savedToken && isTokenValid()) {
        return JSON.parse(savedUser);
      }
    } catch (_) { }
    return null;
  });
  const [userInitialized, setUserInitialized] = useState(false);

  // Global modal states - optimized for performance
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [upgradeModalSection, setUpgradeModalSection] = useState(null);
  const [supportTickets, setSupportTickets] = useState([]);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const loadUser = () => {
      const savedUser = localStorage.getItem('user');
      const savedToken = localStorage.getItem('token');

      if (savedUser && savedToken) {
        try {
          const userData = JSON.parse(savedUser);
          // Check if token is still valid
          if (isTokenValid()) {
            setUser(userData);
          } else {
            // Token expired, clear everything
            localStorage.removeItem('user');
            localStorage.removeItem('token');
            setUser(null);
          }
        } catch (error) {
          console.error('Error parsing user data:', error);
          localStorage.removeItem('user');
          localStorage.removeItem('token');
          setUser(null);
        }
      } else {
        setUser(null);
      }
      setUserInitialized(true);
    };

    loadUser();
  }, []);

  // Load support tickets when user is available and expose the same refresh
  // function to the dashboard so a ticket update is reflected immediately.
  const fetchSupportTickets = useCallback(async () => {
      if (user && isTokenValid()) {
        try {
          const response = await fetch(getApiUrl('/api/support/user/support-tickets?limit=1000'), {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
          });

          if (response.ok) {
            const data = await response.json();
            setSupportTickets(data.supportTickets || []);
          } else {
            setSupportTickets([]);
          }
        } catch (error) {
          console.error('Error fetching support tickets:', error);
          setSupportTickets([]);
        }
      } else {
        setSupportTickets([]);
      }
  }, [user]);

  useEffect(() => {
    fetchSupportTickets();
  }, [fetchSupportTickets]);

  // Hash fragment handling for section navigation
  useEffect(() => {
    const handleHashChange = () => {
      const hash = window.location.hash.substring(1);
      if (hash && window.location.pathname === '/') {
        setTimeout(() => {
          const element = document.getElementById(hash);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth' });
          }
        }, 100);
      }
    };

    // Handle initial hash
    handleHashChange();

    // Listen for hash changes
    window.addEventListener('hashchange', handleHashChange);

    return () => {
      window.removeEventListener('hashchange', handleHashChange);
    };
  }, []);

  // Global modal handlers - optimized for performance
  const handleFeedbackSubmit = useCallback(async (formData) => {
    try {
      const response = await fetch(getApiUrl('/api/support/feedback'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(formData)
      });

      if (!response.ok) {
        throw new Error('Failed to submit feedback');
      }

      setShowFeedbackModal(false);
    } catch (error) {
      console.error('Error submitting feedback:', error);
    }
  }, []);

  const handleUpgradeSuccess = useCallback(() => {
    setShowUpgradeModal(false);
  }, []);

  const handleShowFeedbackModal = useCallback(() => {
    setShowFeedbackModal(true);
  }, []);

  const handleUpgradeClick = useCallback((section) => {
    setUpgradeModalSection(section);
    setShowUpgradeModal(true);
  }, []);

  const handleShowLogoutDialog = useCallback(() => {
    // This will be handled by individual components
    console.log('Logout dialog requested');
  }, []);

  const handleLogin = (userData) => {
    console.log('Login successful, user data:', userData);
    setUser(userData);
  };

  const handleRegister = (userData) => {
    setUser(userData);
  };

  const handleLogout = () => {
    console.log('Logging out...');
    localStorage.removeItem('user');
    localStorage.removeItem('token');
    setUser(null);
    navigate('/');
  };

  const handleGenerateDocument = () => {
    // Navigate to generator page
    navigate('/generator');
  };

  // Protected Route component
  const ProtectedRoute = ({ children, allowedRoles = ['user', 'admin'] }) => {
    if (!userInitialized) {
      return null;
    }
    // Only use the user state, don't call getUser() to prevent infinite loops
    if (!user) {
      return <Navigate to="/login" replace />;
    }

    if (!allowedRoles.includes(user.role)) {
      return <Navigate to="/" replace />;
    }

    return children;
  };

  return (
    <div className="App min-h-screen flex flex-col">
      <Routes>
        <Route path="/" element={
          <Home user={user} />
        } />
        <Route path="/result/:id" element={
          <>
            <Header user={user} onLogout={handleLogout} />
            <main className="flex-grow">
              <Result />
            </main>
            <Footer />
          </>
        } />
        <Route path="/result/payment-success" element={
          <>
            <Header user={user} onLogout={handleLogout} />
            <main className="flex-grow">
              <Result />
            </main>
            <Footer />
          </>
        } />
        <Route path="/result/payment-failed" element={
          <>
            <Header user={user} onLogout={handleLogout} />
            <main className="flex-grow">
              <Result />
            </main>
            <Footer />
          </>
        } />
        <Route path="/about" element={
          <>
            <Header user={user} onLogout={handleLogout} />
            <main className="flex-grow">
              <About />
            </main>
            <Footer />
          </>
        } />
        <Route path="/contact" element={
          <>
            <Header user={user} onLogout={handleLogout} />
            <main className="flex-grow">
              <Contact />
            </main>
            <Footer />
          </>
        } />
        <Route path="/dashboard-overview" element={
          <>
            <Header user={user} onLogout={handleLogout} />
            <main className="flex-grow">
              <DashboardPage />
            </main>
            <Footer />
          </>
        } />
        <Route
          path="/login"
          element={
            user ? <Navigate to="/dashboard" replace /> :
              <Login onLogin={handleLogin} />
          }
        />
        <Route
          path="/register"
          element={
            user ? <Navigate to="/dashboard" replace /> :
              <Register onRegister={handleRegister} />
          }
        />
        <Route path="/verify-email" element={<VerifyEmail />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/unsubscribe" element={<UnsubscribeSimple />} />
        <Route path="/unsubscribe-original" element={<Unsubscribe />} />
        <Route path="/unsubscribe-test" element={<UnsubscribeTest />} />
        <Route path="/landing/:url" element={<LandingPageView />} />
        <Route path="/landing/:url/:tab" element={<LandingPageRoute />} />
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              {new URLSearchParams(location.search).get('tab') === 'settings' ? (
                <Navigate to="/dashboard/settings" replace />
              ) : user?.role === 'admin' ? (
                <Navigate to="/admin" replace />
              ) : (
                <UserDashboard
                  user={user}
                  onLogout={handleLogout}
                  onGenerateDocument={handleGenerateDocument}
                  supportTickets={supportTickets}
                  onSupportTicketsRefresh={fetchSupportTickets}
                />
              )}
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/*"
          element={
            <ProtectedRoute allowedRoles={['admin']}>
              <AdminRouter user={user} onLogout={handleLogout} />
            </ProtectedRoute>
          }
        />
        <Route
          path="/dashboard/projects"
          element={
            <ProtectedRoute>
              <UserDashboard
                user={user}
                onLogout={handleLogout}
                onGenerateDocument={handleGenerateDocument}
                supportTickets={supportTickets}
              />
            </ProtectedRoute>
          }
        />
        <Route
          path="/dashboard/documents"
          element={
            <ProtectedRoute>
              <UserDashboard
                user={user}
                onLogout={handleLogout}
                onGenerateDocument={handleGenerateDocument}
                supportTickets={supportTickets}
              />
            </ProtectedRoute>
          }
        />
        <Route
          path="/dashboard/support"
          element={
            <ProtectedRoute>
              <UserDashboard
                user={user}
                onLogout={handleLogout}
                onGenerateDocument={handleGenerateDocument}
                supportTickets={supportTickets}
              />
            </ProtectedRoute>
          }
        />
        <Route
          path="/dashboard/support/tickets"
          element={
            <ProtectedRoute>
              <UserDashboard
                user={user}
                onLogout={handleLogout}
                onGenerateDocument={handleGenerateDocument}
                supportTickets={supportTickets}
              />
            </ProtectedRoute>
          }
        />
        <Route
          path="/dashboard/settings"
          element={
            <ProtectedRoute>
              <UserDashboard
                user={user}
                onLogout={handleLogout}
                onGenerateDocument={handleGenerateDocument}
                supportTickets={supportTickets}
              />
            </ProtectedRoute>
          }
        />
        <Route
          path="/dashboard/projects/:projectId"
          element={
            <ProtectedRoute>
              <ProjectDetail />
            </ProtectedRoute>
          }
        />
        <Route
          path="/dashboard/shortlinks"
          element={
            <ProtectedRoute>
              <DashboardLayout
                user={user}
                onLogout={handleLogout}
                headerProps={{ customTitle: 'Short Links' }}
                onShowFeedbackModal={handleShowFeedbackModal}
                onUpgradeClick={handleUpgradeClick}
                onShowLogoutDialog={handleShowLogoutDialog}
                supportTickets={supportTickets}
              >
                <ShortLinks
                  user={user}
                  onLogout={handleLogout}
                  onStatsUpdate={() => { }}
                  onShowFeedbackModal={handleShowFeedbackModal}
                  onUpgradeClick={handleUpgradeClick}
                />

                {/* Global Modals */}
                <UpgradeModal
                  isOpen={showUpgradeModal}
                  onClose={() => setShowUpgradeModal(false)}
                  user={user}
                  onUpgrade={handleUpgradeSuccess}
                  highlightSection={upgradeModalSection}
                />

                <FeedbackSupportModal
                  isOpen={showFeedbackModal}
                  onClose={() => setShowFeedbackModal(false)}
                  onSubmit={handleFeedbackSubmit}
                  user={user}
                />
              </DashboardLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/dashboard/shortlinks/:shortLinkId"
          element={
            <ProtectedRoute>
              <DashboardLayout
                user={user}
                onLogout={handleLogout}
                headerProps={{ customTitle: 'Short Link Detail', showBackButton: true, onBackClick: () => navigate('/dashboard/shortlinks') }}
                onShowLogoutDialog={handleShowLogoutDialog}
                supportTickets={supportTickets}
              >
                <ShortLinkDetail user={user} onLogout={handleLogout} />
              </DashboardLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/dashboard/internal-ads"
          element={
            <ProtectedRoute>
              <DashboardLayout
                user={user}
                onLogout={handleLogout}
                headerProps={{ customTitle: 'Internal Ads' }}
                onShowFeedbackModal={handleShowFeedbackModal}
                onUpgradeClick={handleUpgradeClick}
                onShowLogoutDialog={handleShowLogoutDialog}
                supportTickets={supportTickets}
              >
                <InternalAds
                  user={user}
                  onLogout={handleLogout}
                  onStatsUpdate={() => { }}
                  onShowFeedbackModal={handleShowFeedbackModal}
                  onUpgradeClick={handleUpgradeClick}
                />

                {/* Global Modals */}
                <UpgradeModal
                  isOpen={showUpgradeModal}
                  onClose={() => setShowUpgradeModal(false)}
                  user={user}
                  onUpgrade={handleUpgradeSuccess}
                  highlightSection={upgradeModalSection}
                />

                <FeedbackSupportModal
                  isOpen={showFeedbackModal}
                  onClose={() => setShowFeedbackModal(false)}
                  onSubmit={handleFeedbackSubmit}
                  user={user}
                />
              </DashboardLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/dashboard/internal-ads/:id"
          element={
            <ProtectedRoute>
              <DashboardLayout
                user={user}
                onLogout={handleLogout}
                headerProps={{
                  customTitle: 'Internal Ads Detail',
                  showBackButton: true,
                  onBackClick: () => navigate('/dashboard/internal-ads')
                }}
                onShowLogoutDialog={handleShowLogoutDialog}
                supportTickets={supportTickets}
              >
                <InternalAdsDetail
                  user={user}
                  onLogout={handleLogout}
                  onStatsUpdate={() => { }}
                />
              </DashboardLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/dashboard/playstore-emails"
          element={
            <ProtectedRoute allowedRoles={['admin']}>
              <PlayStoreEmails />
            </ProtectedRoute>
          }
        />
        <Route
          path="/playstore-contact"
          element={
            <>
              <Header user={user} onLogout={handleLogout} />
              <main className="flex-grow">
                <PlayStoreContact />
              </main>
            </>
          }
        />
        <Route
          path="/dashboard/projects/:projectId/landing-pages"
          element={
            <ProtectedRoute>
              <DashboardLayout
                user={user}
                onLogout={handleLogout}
                headerProps={{
                  customTitle: 'Landing Pages',
                  showBackButton: true,
                  onBackClick: () => navigate('/dashboard/projects')
                }}
                onShowLogoutDialog={handleShowLogoutDialog}
                supportTickets={supportTickets}
              >
                <LandingPages />
              </DashboardLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/dashboard/landing-pages"
          element={
            <ProtectedRoute>
              <DashboardLayout
                user={user}
                onLogout={handleLogout}
                headerProps={{ customTitle: 'Landing Pages' }}
                onShowFeedbackModal={handleShowFeedbackModal}
                onUpgradeClick={handleUpgradeClick}
                onShowLogoutDialog={handleShowLogoutDialog}
                supportTickets={supportTickets}
              >
                <LandingPages
                  onShowFeedbackModal={handleShowFeedbackModal}
                  onUpgradeClick={handleUpgradeClick}
                />

                {/* Global Modals */}
                <UpgradeModal
                  isOpen={showUpgradeModal}
                  onClose={() => setShowUpgradeModal(false)}
                  user={user}
                  onUpgrade={handleUpgradeSuccess}
                  highlightSection={upgradeModalSection}
                />

                <FeedbackSupportModal
                  isOpen={showFeedbackModal}
                  onClose={() => setShowFeedbackModal(false)}
                  onSubmit={handleFeedbackSubmit}
                  user={user}
                />
              </DashboardLayout>
            </ProtectedRoute>
          }
        />
      </Routes>
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 4000,
          style: {
            background: '#363636',
            color: '#fff',
          },
          success: {
            style: {
              background: '#10b981',
            },
          },
          error: {
            style: {
              background: '#ef4444',
            },
          },
        }}
      />

      {/* Modals are now handled per route */}
    </div>
  );
}

export default App; 
